class GltheaaSizeChart extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-size-chart-data]');
    if (!dataEl) return;

    try {
      this.chart = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    if (!this.chart || !Array.isArray(this.chart.sizes) || !Array.isArray(this.chart.points)) return;

    this.locale = this.dataset.locale === 'ja' ? 'ja' : 'en';
    this.unit = this.chart.unit || '';
    this.switcher = this.querySelector('.gltheaa-size-chart__size-switch');
    this.indicator = this.querySelector('.gltheaa-size-chart__size-indicator');
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size-chart-size]'));
    this.onSizeClick = this.onSizeClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onIndicatorEnd = this.onIndicatorEnd.bind(this);

    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', this.onSizeClick);
    });
    this.indicator?.addEventListener('transitionend', this.onIndicatorEnd);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    this.selectSize(this.dataset.selectedSize || this.chart.sizes[0], false);
    this.classList.add('is-ready');

    window.addEventListener('resize', this.onResize);
    if (this.switcher && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.selectedSize || this.indicator?.classList.contains('is-moving')) return;
        this.moveIndicator(this.activeButton(), false);
      });
      this.resizeObserver.observe(this.switcher);
    }
  }

  disconnectedCallback() {
    this.sizeButtons?.forEach((button) => {
      button.removeEventListener('click', this.onSizeClick);
    });
    window.removeEventListener('resize', this.onResize);
    this.indicator?.removeEventListener('transitionend', this.onIndicatorEnd);
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.switchTimer);
    this.variantUnsubscriber?.();
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onSizeClick(event) {
    this.selectSize(event.currentTarget.getAttribute('data-size-chart-size'));
  }

  onVariantChange(event) {
    if (event.data?.sectionId !== this.dataset.sectionId) return;
    const options = event.data?.variant?.options;
    if (!Array.isArray(options)) return;

    const match = options.find((option) => this.chart.sizes.includes(option));
    if (match) this.selectSize(match);
  }

  onResize() {
    if (!this.selectedSize) return;
    this.moveIndicator(this.activeButton(), false);
  }

  onIndicatorEnd(event) {
    if (event.propertyName !== 'transform' && event.propertyName !== 'width') return;
    this.indicator?.classList.remove('is-moving');
  }

  activeButton() {
    return this.sizeButtons.find((button) => button.getAttribute('aria-pressed') === 'true');
  }

  pointLabel(point) {
    const label = point?.label || {};
    return label[this.locale] || label.en || label.ja || point?.key || '';
  }

  pointValue(point, size) {
    const values = point?.values || {};
    const value = values[size];
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  moveIndicator(button, animate = true) {
    if (!this.indicator || !this.switcher || !button) return;

    const listRect = this.switcher.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const left = buttonRect.left - listRect.left + this.switcher.scrollLeft;
    const top = buttonRect.top - listRect.top + this.switcher.scrollTop;

    this.indicator.classList.toggle('is-moving', Boolean(animate) && !this.prefersReducedMotion());
    this.indicator.style.width = `${buttonRect.width}px`;
    this.indicator.style.height = `${buttonRect.height}px`;
    this.indicator.style.transform = `translate(${left}px, ${top}px)`;
  }

  writeValues(size) {
    this.chart.points.forEach((point) => {
      const label = this.pointLabel(point);
      const value = this.pointValue(point, size);
      const displayValue = value && this.unit ? `${value}${this.unit}` : value;

      const pointKey = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(point.key) : point.key;
      this.querySelectorAll(`[data-size-point="${pointKey}"]`).forEach((node) => {
        const labelEl = node.querySelector('[data-size-label]');
        const valueEl = node.querySelector('[data-size-value]');
        if (labelEl) labelEl.textContent = label;
        if (valueEl) valueEl.textContent = displayValue;
      });
    });
  }

  selectSize(size, animate = true) {
    if (!this.chart.sizes.includes(size)) return;
    if (size === this.selectedSize && this.classList.contains('is-ready')) return;

    this.selectedSize = size;
    const shouldAnimate = Boolean(animate) && this.classList.contains('is-ready') && !this.prefersReducedMotion();

    this.sizeButtons.forEach((button) => {
      const pressed = button.getAttribute('data-size-chart-size') === size;
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });

    this.moveIndicator(this.activeButton(), shouldAnimate);

    this.querySelectorAll('[data-size-col]').forEach((cell) => {
      cell.classList.toggle('is-active', cell.getAttribute('data-size-col') === size);
    });

    window.clearTimeout(this.switchTimer);
    if (shouldAnimate) {
      this.classList.add('is-switching');
      this.switchTimer = window.setTimeout(() => {
        this.writeValues(size);
        this.classList.remove('is-switching');
      }, 180);
      return;
    }

    this.classList.remove('is-switching');
    this.writeValues(size);
  }
}

if (!customElements.get('gltheaa-size-chart')) {
  customElements.define('gltheaa-size-chart', GltheaaSizeChart);
}
